import React from 'react';
import { fetchListIds } from '../FindDuplicateContacts/fetchFilteredIds';
import startContactMailMerge, { MAIL_MERGE_MAX_CONTACTS } from '../MailMerge/startContactMailMerge';
import { markSelected, pageSelectedCount, selectionSignature, toggleSelection } from './selection';

/**
 * The selection and mail merge the Agents and Vendors lists share.
 *
 * A subclass names its list in mailMergeList() — {audience, noun, plural,
 * queryName, filterKey} — calls beginSelectionFetch(query) as each fetch
 * starts and withSelection(rows) on the rows it brings back, and renders
 * selectionTableProps() onto its SortTable and selectionBarProps() onto
 * ContactSelectionBar.
 */
export default class SelectableContactList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: null,
            totalCount: null,
            selectedIds: new Set(),
            selectingAll: false,
            preparingMailMerge: false,
            mailMergeError: null,
        };
        this._selectionSignature = null;

        this.toggleCheckbox = this.toggleCheckbox.bind(this);
        this.selectAllMatching = this.selectAllMatching.bind(this);
        this.clearSelection = this.clearSelection.bind(this);
        this.startMailMerge = this.startMailMerge.bind(this);
    }

    /** @returns {{audience: string, noun: string, plural: string, queryName: string, filterKey: string}} */
    mailMergeList() {
        throw new Error('mailMergeList() is not defined.');
    }

    /** A different filter is a different list, and the ticks go with it. */
    beginSelectionFetch(query) {
        const signature = selectionSignature(query);
        if (signature === this._selectionSignature) return;

        this._selectionSignature = signature;
        this.setState({ selectedIds: new Set() });
    }

    withSelection(rows) {
        return markSelected(rows, this.state.selectedIds);
    }

    toggleCheckbox(e) {
        this.setState(toggleSelection(this.state.items, this.state.selectedIds, e.target.name, e.target.checked));
    }

    /** Ticks every row the filter matches, not just the page on screen. */
    selectAllMatching() {
        const { noun, plural, queryName, filterKey } = this.mailMergeList();
        const query = this.props.location.query;
        const signature = selectionSignature(query);

        this.setState({ selectingAll: true, mailMergeError: null });

        fetchListIds(queryName, filterKey, query, {
            limit: MAIL_MERGE_MAX_CONTACTS + 1,
            tooMany: `A mail merge reaches up to ${MAIL_MERGE_MAX_CONTACTS.toLocaleString()} ${plural}, and this `
                + `filter matches more ${plural} than that. Narrow it to select every ${noun}.`,
        }).then((ids) => {
            // The filter changed while the ids were read; they belong to another list.
            if (signature !== this._selectionSignature) return this.setState({ selectingAll: false });

            const selectedIds = new Set(ids.map(String));
            this.setState({
                selectedIds,
                items: markSelected(this.state.items, selectedIds),
                selectingAll: false,
            });
        }).catch((err) => this.setState({
            selectingAll: false,
            mailMergeError: err.message || 'Could not read this list.',
        }));
    }

    clearSelection() {
        const selectedIds = new Set();
        this.setState({ selectedIds, items: markSelected(this.state.items, selectedIds) });
    }

    /** The ticked rows, or every row the filter matches when none are ticked. */
    startMailMerge() {
        const { audience, noun, queryName, filterKey } = this.mailMergeList();
        this.setState({ preparingMailMerge: true, mailMergeError: null });

        startContactMailMerge({
            audience,
            noun,
            queryName,
            filterKey,
            query: this.props.location.query,
            selectedIds: Array.from(this.state.selectedIds),
        }).catch((err) => this.setState({
            preparingMailMerge: false,
            mailMergeError: err.message || 'Could not read this list.',
        }));
    }

    mailMergeLabel() {
        if (this.state.preparingMailMerge) return 'Preparing…';

        const count = this.state.selectedIds.size;
        return count ? `Mail Merge (${count.toLocaleString()})` : 'Mail Merge';
    }

    mailMergeTitle() {
        const { noun, plural } = this.mailMergeList();
        return this.state.selectedIds.size
            ? `Email the ${plural} you ticked`
            : `Email every ${noun} this list matches`;
    }

    selectionTableProps() {
        const items = this.state.items || [];

        return {
            toggleCheckbox: this.toggleCheckbox,
            toggleAllCheckbox: this.toggleCheckbox,
            selectedAll: items.length > 0 && pageSelectedCount(items) === items.length,
        };
    }

    selectionBarProps() {
        const { noun, plural } = this.mailMergeList();
        const items = this.state.items || [];

        return {
            noun,
            plural,
            selectedCount: this.state.selectedIds.size,
            pageSelected: pageSelectedCount(items),
            pageCount: items.length,
            totalCount: this.state.totalCount,
            selectingAll: this.state.selectingAll,
            onSelectAll: this.selectAllMatching,
            onClear: this.clearSelection,
        };
    }
}
